import CircularProgress from '@mui/material/CircularProgress'
import clsx from 'clsx'
import { useRef } from 'react'
import { useAppSelector, useIntersectionObserver } from 'shared/hooks'
import typo from 'shared/styles/typography.module.scss'

import type { GetProps } from '../../model/types'

import emptySnail from '../../../../assets/image/snail-no.png'
import { FullLoader } from '../../../../components/atoms/FullLoader/FullLoader'
import { api } from '../../api'
import { useCurrentId } from '../../hooks'
import { useSelectFirstChat } from '../../hooks'
import { selectors } from '../../utils/selectors'
import { PULLING_OPTIONS } from '../const'
import { useIsPulling } from '../FilterForm/TempDebug'
import { Thumbnail } from '../Thumbnail/Thumbnail'
import css from './Conversations.module.scss'

const EMPTY = (
	<figure className={clsx(css.empty, typo.buttonSmB)}>
		<img alt='Snail show stop sign' height={209} src={emptySnail} width={120} />
		<figcaption>No results found</figcaption>
	</figure>
)

export function Conversations() {
	const filter = useAppSelector(selectors.filter),
		search = useAppSelector(selectors.search),
		{ conversations, nextCursor } = useAppSelector(
			selectors.showedConversations
		)

	const props: GetProps = { filter, search }

	const mailboxRef = useRef<HTMLUListElement | null>(null),
		prevProps = useRef<GetProps>(props)

	const [chatSpinner, entry] = useIntersectionObserver({
		root: mailboxRef.current
	})
	const current = useCurrentId()

	const isPulling = useIsPulling()

	const settingQuery = api.useSettingsQuery()
	const { isFetching, isLoading, isSuccess } = api.useConversationsQuery(
		props,
		isPulling
			? {
					...PULLING_OPTIONS,
					skip: !settingQuery.isSuccess
				}
			: {
					skip: !settingQuery.isSuccess
				}
	)

	const shouldFetchNext =
		settingQuery.isSuccess && nextCursor && entry?.isIntersecting

	api.useConversationsNextQuery(
		{ ...props, nextCursor },
		{
			skip: !shouldFetchNext
		}
	)

	const isChangedProps = !(
		prevProps.current.filter === filter && prevProps.current.search === search
	)

	useSelectFirstChat(conversations)

	const shouldShowLoader =
			isLoading ||
			Boolean(isChangedProps && isFetching) ||
			settingQuery.isLoading,
		isReady = isSuccess && !shouldShowLoader

	if (!shouldShowLoader) {
		prevProps.current = props
	}

	if (shouldShowLoader) {
		return <FullLoader className={css.loader} size={40} />
	}

	if (isReady && conversations.length === 0) {
		return EMPTY
	}

	return (
		<ul className={`app-scroll ${css.chats}`} id='mailbox' ref={mailboxRef}>
			{conversations.map(conversation => (
				<Thumbnail
					id={conversation.entity_id}
					isSelected={conversation.entity_id === current}
					key={conversation.entity_id}
				/>
			))}

			{nextCursor && (
				<li className={css.nextLoader} ref={chatSpinner}>
					<CircularProgress
						classes={{ colorPrimary: 'material-loader' }}
						color='primary'
						size={30}
					/>
				</li>
			)}
		</ul>
	)
}
